import type { ComponentDocs } from '../component-docs.js';
import type { CaosLookupOption, CaosLookupSearch } from './lookup-field.js';

/**
 * What `<caos-lookup-field>` says about itself.
 *
 * TWO HALVES, AND THE INTERESTING ONE IS EDIT. In read mode this is a link carrying the linked
 * record's NAME — never its id. In edit mode it is a typeahead over a listbox, with a clear
 * control for un-pointing it. Read mode on its own hides everything the piece actually does, which
 * is why the examples below show both.
 *
 * RESULTS COME THROUGH AN INJECTED SEAM. `search(query) => options` is set as a property; this
 * layer never talks to a server or to the access engine. That is deliberate — the piece stays a
 * presentational leaf with no data dependency baked into it — but it means an unhosted example
 * draws a typeahead that finds nothing, which is a working example teaching that the piece does
 * nothing. So the edit example below declares a `host`: a placement carries stored values and a
 * function is not one, so the surrounding page supplies the search and the page says so.
 *
 * WHAT THE SEARCH SEAM IS RESPONSIBLE FOR. The options this control offers must already be only
 * the records the current person may READ — object, record and field visibility. The control is
 * access-agnostic on purpose; the app binds an access-scoped source into this seam. Anything that
 * comes back is offered.
 *
 * WHAT A PAGE CAN READ BACK. The piece exposes a read-only `open` property telling a page whether
 * the list is showing. It is neither an input nor a method — nothing can set it and there is no call
 * to make — so it is declared under `state`, the section for exactly that.
 */

/** What the surrounding page searches in the edit example. A local list, where an app binds a real source. */
const ACCOUNTS: readonly CaosLookupOption[] = [
  { value: 'acc-meridian', label: 'Meridian Supply', sublabel: 'Customer', href: '#' },
  { value: 'acc-calder', label: 'Calder & Finch', sublabel: 'Partner', href: '#' },
  { value: 'acc-brightwater', label: 'Brightwater Group', sublabel: 'Customer', href: '#' },
];

const searchAccounts: CaosLookupSearch = (query) =>
  ACCOUNTS.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()));

const docs: ComponentDocs = {
  aliases: ['lookup', 'reference field', 'relationship field', 'record picker', 'typeahead', 'autocomplete', 'foreign key field'],

  answers: [
    'point a record at another record',
    'let somebody search for an account instead of picking from a list',
    'show which record this one is linked to, as a link',
    're-point a link that is already set',
    'clear a link without setting a different one',
  ],

  placements: ['recordPage', 'appPage', 'screen'],

  inputs: [
    {
      name: 'mode',
      attribute: 'mode',
      type: 'text',
      options: ['read', 'edit'],
      fallback: 'read',
      description: 'Which half to draw: `read` for the link, `edit` for the typeahead. Anything else reads as `read`.',
      default: 'read',
    },
    {
      name: 'search',
      // No attribute: the seam is a function, and there is no honest way to put one in a string.
      // The closed input-type set has no member for a function; `json` is the nearest it offers.
      type: 'json',
      description:
        'The result source — `(query, request) => options` or a promise of them, where an option is ' +
        '`{ value, label, sublabel?, href?, fields? }`. The second argument carries what the surface asked ' +
        'for, including the fields the query should be matched against; a source written for the one-argument ' +
        'shape keeps working and simply ignores it. Set it as a property. Whatever it returns is offered, so ' +
        'it is this function that has to be scoped to what the current person may read.',
    },
    {
      name: 'matchFields',
      attribute: 'match-fields',
      type: 'text',
      description:
        'Which fields the typing is matched against, as a comma-separated list handed straight to the search ' +
        'seam — "name, account_number". A lookup that matches only on a name cannot find a record by its ' +
        'number, and the surface is what knows which is which. Saying nothing leaves the decision to the ' +
        'source, which is what every source decided on its own before.',
    },
    {
      name: 'displayFields',
      attribute: 'display-fields',
      type: 'text',
      description:
        'Which of a result\'s own fields are drawn on its second line, as a comma-separated list — ' +
        '"industry, city" — joined in the order given. This is how two records with the same name are told ' +
        'apart. An option that supplies an explicit sublabel keeps it: a source that has already decided what ' +
        'the second line says was not asked to reconsider.',
    },
    {
      name: 'minChars',
      attribute: 'min-chars',
      type: 'number',
      description:
        'How much has to be typed before the source is asked at all. Under it the list says what is needed ' +
        'rather than offering everything. Zero — ask from the first keystroke, and from an empty box — is what ' +
        'this control did before and is still the default.',
      default: 0,
    },
    {
      name: 'searchDelay',
      attribute: 'search-delay',
      type: 'number',
      description:
        'Milliseconds to wait after the last keystroke before asking, so a fast typist makes one request ' +
        'rather than eight. Raise it for a source that is expensive to ask.',
      default: 150,
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'recordId',
      description: 'The id of the record pointed at. Written back when an option is chosen, and removed on clear.',
      // Two-way bound to a field on the record being edited — the value it holds IS the record's
      // own lookup field, not a literal an author types once when placing the piece (CAOS-562).
      source: 'recordField',
    },
    {
      name: 'display',
      attribute: 'display',
      property: true,
      type: 'text',
      description:
        "The linked record's name — what read mode actually draws. Without it the link falls back to showing " +
        'the raw id, which is the one thing this piece exists to avoid.',
    },
    {
      name: 'href',
      attribute: 'href',
      type: 'text',
      description:
        'Where the read-mode link goes. Left unset the name is still drawn and still reachable by keyboard, as a ' +
        'trigger for re-pointing rather than as navigation.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        "The field's name, drawn above the control and pointed at the input, so clicking it focuses the " +
        'typeahead. It is also used as the accessible name.',
    },
    {
      name: 'placeholder',
      attribute: 'placeholder',
      type: 'text',
      description:
        'Two jobs, one word: the hint inside the empty typeahead in edit mode, and what read mode shows when ' +
        'nothing is linked. Read mode falls back to an em dash.',
      default: 'Search…',
    },
    {
      name: 'required',
      attribute: 'required',
      type: 'boolean',
      description: 'A record has to be pointed at. Draws the asterisk beside the label.',
      default: false,
    },
    {
      name: 'disabled',
      attribute: 'disabled',
      type: 'boolean',
      description: 'The link may not be changed. Greys the typeahead and refuses focus. It does not affect read mode.',
      default: false,
    },
    {
      name: 'invalid',
      attribute: 'invalid',
      type: 'boolean',
      description: 'The link is wrong or missing. Turns the outline and the message red and marks the input invalid.',
      default: false,
    },
    {
      name: 'message',
      attribute: 'message',
      type: 'text',
      description: 'The line under the control — muted helper text on its own, the red reason when `invalid` is also set.',
    },
    {
      name: 'inline',
      attribute: 'inline',
      type: 'boolean',
      description:
        'Draw the record-detail variant: no box, an accent underline, inheriting the surrounding type, with the ' +
        'search glyph and the clear control pulled to the edges. The label and the message are hidden.',
      default: false,
    },
  ],

  events: [
    {
      name: 'caos-lookup-change',
      when: 'When an option is chosen — clicked, or Enter on the active one. The list closes and the input takes the chosen name.',
      carries: '`{ value, label }` — the id now pointed at and the name being shown for it.',
    },
    {
      name: 'caos-lookup-clear',
      when: 'When the clear control is pressed. The link is dropped, the input is emptied and focus returns to it.',
      carries: 'nothing',
    },
  ],

  // Nothing goes inside it: the shadow root holds the label, both views and the listbox, and there
  // is no <slot> anywhere. The options are data, not content.
  // `value` and `display` are readable too, but a page can set them, so they are inputs. `open` is the
  // one it can only ask about.
  state: [
    {
      name: 'open',
      type: 'boolean',
      description: 'Whether the results list is showing.',
    },
  ],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface of the label, the link, the typeahead and the message.' },
    { token: '--caos-text-body', controls: 'The size of the label, the link and the typed query.' },
    { token: '--caos-text-row', controls: 'The size of an option label in the list.' },
    { token: '--caos-space-1', controls: 'The gap between the label, the control and the message, and the padding inside the list.' },
    { token: '--caos-space-2', controls: 'The inset of the clear control, and the vertical padding of an option.' },
    { token: '--caos-space-3', controls: 'The padding inside the typeahead and the horizontal padding of an option.' },
    { token: '--caos-space-4', controls: 'The left inset of the typeahead in the inline variant, clearing its search glyph.' },
    { token: '--caos-space-5', controls: 'With the smaller spaces, the room the typeahead keeps for its glyph and its clear control.' },
    { token: '--caos-radius', controls: 'The corner radius of the typeahead and of the results list.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of an option, of the clear control and of the link focus ring.' },
    { token: '--caos-color-border', controls: 'The outline of the typeahead at rest, and the edge of the results list.' },
    { token: '--caos-color-border-strong', controls: 'The outline of the typeahead while it has focus.' },
    { token: '--caos-color-surface', controls: 'The fill of the results list, and the ground the selected option is mixed into.' },
    { token: '--caos-color-surface-2', controls: 'The fill of the typeahead, and the highlight on the option the keyboard is on.' },
    { token: '--caos-color-text', controls: 'The typed query and an option label.' },
    { token: '--caos-color-text-muted', controls: 'The field label, the search glyph, the placeholder, an option sublabel, the status line and the message.' },
    { token: '--caos-color-accent', controls: 'The hover outline, the underline of the inline variant, and the tint on the already-chosen option.' },
    { token: '--caos-color-accent-strong', controls: 'The colour of the read-mode link.' },
    { token: '--caos-accent-wash', controls: 'The tint that fills the typeahead while it has focus, in place of a focus ring.' },
    { token: '--caos-focus-ring', controls: 'The ring on the read-mode link and on the clear control.' },
    { token: '--caos-shadow-float', controls: 'The shadow that lifts the results list off the page.' },
    { token: '--caos-color-danger', controls: 'The required asterisk, and the outline and message when invalid.' },
  ],

  parts: [
    { part: 'label', is: 'the label above the control' },
    { part: 'link', is: 'the linked record in read mode' },
    { part: 'combo', is: 'the combobox in edit mode' },
    { part: 'icon', is: 'the search glyph inside the box' },
    { part: 'input', is: 'the text input' },
    { part: 'clear', is: 'the control that clears the selection' },
    { part: 'listbox', is: 'the results list' },
    { part: 'option', is: 'one result' },
    { part: 'message', is: 'the line under the control' },
  ],

  methods: [
    {
      name: 'focus',
      description: 'Move keyboard focus to the typeahead. Focusing it re-runs the search when there is already a query in it, so the list comes back rather than staying shut.',
      args: [
        {
          name: 'options',
          type: 'FocusOptions',
          description: 'The ordinary focus options — pass `{ preventScroll: true }` to focus without scrolling to it.',
        },
      ],
    },
  ],

  placing:
    'Placed as an item in a page section like any other field, with two things the page owes it. The search ' +
    'seam, without which the typeahead finds nothing. And ROOM BELOW: the results list is positioned inside ' +
    'the field rather than in the page top layer, so anything between it and the viewport that clips its ' +
    'overflow will cut the list off. A field near the bottom of a scrolling panel is where this shows.',

  whenToUse:
    'The value is another RECORD, and there are too many of them to list. Read mode wherever a record shows what ' +
    'it is linked to; edit mode wherever that link can be changed.',

  insteadUse: [
    { name: 'PicklistField', when: 'The options are a short fixed list that can simply be handed over, rather than something searched.' },
    {
      name: 'FieldRenderer',
      when: 'The type is not known until the record is read — hand it the field metadata and it draws this piece for a Reference, in whichever mode it was given.',
    },
    { name: 'KeyValueRow', when: 'The link only has to be read and never re-pointed, and it sits among other read-only rows.' },
    { name: 'ObjectCaret', when: 'The person is navigating to a record rather than choosing one to store on this one.' },
    { name: 'TextField', when: 'What is being captured is somebody else\'s reference number, not a record in this system.' },
  ],

  accessibility:
    'The typeahead is a real combobox: `role="combobox"` with `aria-expanded`, `aria-controls` and ' +
    '`aria-autocomplete="list"` over a `role="listbox"` of options, and the arrow keys move an ' +
    '`aria-activedescendant` rather than moving DOM focus, so what the person is typing never leaves the box. ' +
    'Enter selects, Escape closes, Home and End jump to the ends, the list scrolls the active option into view, ' +
    'and the list closes when focus leaves the whole piece — including out of the shadow root, which is checked ' +
    'through the deep active element rather than assumed. The clear control carries its own name, the input has ' +
    'a generated id its label points at, and the searching and no-matches lines are real content in the list ' +
    'rather than an empty box. What is left to the author is the option LABELS and sublabels — two accounts with ' +
    'the same name are told apart only by the sublabel — and `display`, without which the link announces an id.',

  examples: [
    {
      key: 'read',
      label: 'The link',
      summary: 'Read mode: the linked record drawn as its name, in the accent, never as the id underneath it.',
      placement: {
        component: 'caos-lookup-field',
        inputs: {
          mode: 'read',
          label: 'Account',
          value: 'acc-meridian',
          display: 'Meridian Supply',
          href: '#',
        },
      },
    },
    {
      key: 'unlinked',
      label: 'Nothing linked yet',
      summary: 'The same read mode with no record pointed at — an em dash, or whatever the placeholder says, rather than an empty gap.',
      placement: [
        { component: 'caos-lookup-field', inputs: { mode: 'read', label: 'Account' } },
        { component: 'caos-lookup-field', inputs: { mode: 'read', label: 'Parent account', placeholder: 'Not set' } },
      ],
    },
    {
      key: 'typeahead',
      label: 'Re-pointing it',
      summary:
        'Edit mode doing its job: type a letter, the results open, the arrow keys roam them, and the clear control ' +
        'appears once something is linked.',
      // Measured against the three-account fixture the surrounding page searches. The list itself is
      // capped at 260px, so a longer result set overlaps the block beneath rather than being clipped.
      stage: { opensBelow: 200 },
      placement: {
        component: 'caos-lookup-field',
        inputs: {
          mode: 'edit',
          label: 'Account',
          placeholder: 'Search accounts…',
          value: 'acc-meridian',
          display: 'Meridian Supply',
        },
      },
      host: {
        summary:
          'supplies the search — three accounts filtered by name. A placement stores values, and a function is not ' +
          'one, so the results always come from the page around this field rather than from the field itself.',
        run: (stage) => {
          const field = stage.querySelector('caos-lookup-field');
          if (field) field.search = searchAccounts;
        },
      },
    },
  ],
};

export default docs;
